import { useEffect, useRef, useState } from "react";

const MAIN_SCROLL_SELECTOR = ".lf-h5-space-main";
const DEFAULT_THRESHOLD = 56;
const MAX_PULL = 88;
const PULL_DAMPING = 0.45;

export type PullRefreshPhase = "idle" | "pulling" | "ready" | "refreshing";

export interface MainScrollPullRefreshState {
  pullDistance: number;
  phase: PullRefreshPhase;
}

/** 在指定滚动容器顶部下拉时触发刷新；默认绑定空间主滚动区 */
export function useMainScrollPullRefresh(
  onRefresh: () => Promise<void>,
  enabled = true,
  scrollSelector: string = MAIN_SCROLL_SELECTOR
): MainScrollPullRefreshState {
  const [pullDistance, setPullDistance] = useState(0);
  const [phase, setPhase] = useState<PullRefreshPhase>("idle");
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const phaseRef = useRef<PullRefreshPhase>("idle");
  const onRefreshRef = useRef(onRefresh);

  onRefreshRef.current = onRefresh;
  pullDistanceRef.current = pullDistance;
  phaseRef.current = phase;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const scrollEl = document.querySelector<HTMLElement>(scrollSelector);
    if (!scrollEl) {
      return undefined;
    }

    const setPhaseSafe = (next: PullRefreshPhase) => {
      phaseRef.current = next;
      setPhase(next);
    };

    const setPullSafe = (dist: number) => {
      pullDistanceRef.current = dist;
      setPullDistance(dist);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (phaseRef.current === "refreshing") {
        return;
      }
      const target = e.target;
      if (
        target instanceof Element &&
        target.closest(".lf-vp-feed__tabs-row, [data-pull-refresh-ignore]")
      ) {
        pullingRef.current = false;
        return;
      }
      if (scrollEl.scrollTop > 0) {
        pullingRef.current = false;
        return;
      }
      startYRef.current = e.touches[0]?.clientY ?? 0;
      pullingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pullingRef.current || phaseRef.current === "refreshing") {
        return;
      }
      if (scrollEl.scrollTop > 0) {
        pullingRef.current = false;
        setPullSafe(0);
        setPhaseSafe("idle");
        return;
      }
      const y = e.touches[0]?.clientY ?? 0;
      const delta = y - startYRef.current;
      if (delta <= 0) {
        setPullSafe(0);
        setPhaseSafe("idle");
        return;
      }
      const dist = Math.min(delta * PULL_DAMPING, MAX_PULL);
      setPullSafe(dist);
      setPhaseSafe(dist >= DEFAULT_THRESHOLD ? "ready" : "pulling");
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) {
        return;
      }
      pullingRef.current = false;
      const dist = pullDistanceRef.current;
      if (dist >= DEFAULT_THRESHOLD && phaseRef.current === "ready") {
        setPhaseSafe("refreshing");
        setPullSafe(DEFAULT_THRESHOLD * 0.75);
        void (async () => {
          try {
            await onRefreshRef.current();
          } finally {
            setPullSafe(0);
            setPhaseSafe("idle");
          }
        })();
        return;
      }
      setPullSafe(0);
      setPhaseSafe("idle");
    };

    scrollEl.addEventListener("touchstart", onTouchStart, { passive: true });
    scrollEl.addEventListener("touchmove", onTouchMove, { passive: true });
    scrollEl.addEventListener("touchend", onTouchEnd, { passive: true });
    scrollEl.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      scrollEl.removeEventListener("touchstart", onTouchStart);
      scrollEl.removeEventListener("touchmove", onTouchMove);
      scrollEl.removeEventListener("touchend", onTouchEnd);
      scrollEl.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, scrollSelector]);

  return { pullDistance, phase };
}
